import { useTranslation } from "react-i18next";

import { Group, Progress, SimpleGrid, Stack, Text, Title } from "@mantine/core";

import { useFormatters } from "@prostocheck/i18n";
import { Card } from "@prostocheck/ui";

import { IPeriodDaysProps } from "./period-days.models";

/**
 * Sales per day as a bar against the best day of the period — the shape of a
 * week matters more here than the exact hryvnia, which is printed beside it.
 */
export const PeriodDays = ({ report }: IPeriodDaysProps) => {
  const { t } = useTranslation();
  const { formatDate, formatNumber, formatPrice } = useFormatters();

  const maxDayTotal = Math.max(...report.days.map(day => day.salesTotal), 1);

  return (
    <Card className="mt-6">
      <Title order={2} size="h5">
        {t("period.byDay")}
      </Title>

      <SimpleGrid
        component="dl"
        cols={2}
        spacing="md"
        mt="lg"
        className="border-b border-ink-900/8 pb-4"
      >
        <div>
          <Text component="dt" size="sm" c="dimmed">
            {t("period.cash")}
          </Text>
          <Text component="dd" size="sm" fw={500} mt={2}>
            {formatPrice(report.payments.cash)}
          </Text>
        </div>
        <div>
          <Text component="dt" size="sm" c="dimmed">
            {t("period.card")}
          </Text>
          <Text component="dd" size="sm" fw={500} mt={2}>
            {formatPrice(report.payments.card)}
          </Text>
        </div>
      </SimpleGrid>

      {report.days.length === 0 ? (
        <Text size="sm" c="dimmed" mt="md">
          {t("period.empty")}
        </Text>
      ) : (
        <Stack gap="md" mt="lg">
          {report.days.map(day => (
            <div key={day.date}>
              <Group justify="space-between" gap="xs" mb={6}>
                <Text size="sm">{formatDate(day.date)}</Text>
                <Text size="sm" c="dimmed">
                  {t("period.dayTotals", {
                    amount: formatPrice(day.salesTotal),
                    count: formatNumber(day.receiptsCount),
                  })}
                </Text>
              </Group>
              <Progress
                value={(day.salesTotal / maxDayTotal) * 100}
                size="sm"
                aria-hidden="true"
              />
            </div>
          ))}
        </Stack>
      )}
    </Card>
  );
};
